// Shared calculation helpers for the invoice components.
// Place this file alongside InvoiceElementore.jsx / InvoiceEdit.jsx
// (e.g. components/Bills/invoiceUtils.js) and import from there.

const GST_RATE = 0.09; // 9% each for CGST and SGST

// Returns whole nights between two "YYYY-MM-DD" date strings, or null if
// either date is missing/invalid or checkout isn't after checkin.
export const calculateNights = (checkin, checkout) => {
  if (!checkin || !checkout) return null;
  const inDate = new Date(checkin);
  const outDate = new Date(checkout);
  if (isNaN(inDate.getTime()) || isNaN(outDate.getTime())) return null;
  const diffDays = Math.round(
    (outDate.getTime() - inDate.getTime()) / (1000 * 60 * 60 * 24),
  );
  return diffDays > 0 ? diffDays : null;
};

// Given a rate and nights, returns { cgst, sgst } each as 9% of (rate * nights),
// formatted to 2 decimal places as strings (ready to drop into a text input).
export const calculateRowGst = (rate, nights) => {
  const rowSubtotal = (parseFloat(rate) || 0) * (parseFloat(nights) || 0);
  if (rowSubtotal <= 0) return null;
  const gstAmount = (rowSubtotal * GST_RATE).toFixed(2);
  return { cgst: gstAmount, sgst: gstAmount };
};

// --- Indian-style number-to-words (Crore / Lakh / Thousand / Hundred) ---

const ONES = [
  "",
  "One",
  "Two",
  "Three",
  "Four",
  "Five",
  "Six",
  "Seven",
  "Eight",
  "Nine",
  "Ten",
  "Eleven",
  "Twelve",
  "Thirteen",
  "Fourteen",
  "Fifteen",
  "Sixteen",
  "Seventeen",
  "Eighteen",
  "Nineteen",
];
const TENS = [
  "",
  "",
  "Twenty",
  "Thirty",
  "Forty",
  "Fifty",
  "Sixty",
  "Seventy",
  "Eighty",
  "Ninety",
];

const twoDigitsToWords = (n) => {
  if (n < 20) return ONES[n];
  const tens = TENS[Math.floor(n / 10)];
  const ones = n % 10 ? ` ${ONES[n % 10]}` : "";
  return `${tens}${ones}`;
};

const threeDigitsToWords = (n) => {
  let result = "";
  if (n >= 100) {
    result += `${ONES[Math.floor(n / 100)]} Hundred`;
    n %= 100;
    if (n) result += " ";
  }
  if (n > 0) result += twoDigitsToWords(n);
  return result;
};

// Converts a non-negative number (decimals are rounded off) to words, e.g.
// 460 -> "FOUR HUNDRED SIXTY"
export const numberToWords = (num) => {
  let n = Math.round(Number(num) || 0);
  if (n === 0) return "ZERO";

  const crore = Math.floor(n / 10000000);
  n %= 10000000;
  const lakh = Math.floor(n / 100000);
  n %= 100000;
  const thousand = Math.floor(n / 1000);
  n %= 1000;
  const hundred = n;

  const parts = [];
  if (crore) parts.push(`${threeDigitsToWords(crore)} Crore`);
  if (lakh) parts.push(`${threeDigitsToWords(lakh)} Lakh`);
  if (thousand) parts.push(`${threeDigitsToWords(thousand)} Thousand`);
  if (hundred) parts.push(threeDigitsToWords(hundred));

  return parts.join(" ").toUpperCase();
};
